document.addEventListener('DOMContentLoaded', function() {
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  let isUpdating = false;
  let updateQueued = false;
  let lastQuantityUpdate = null;
  let updateTimeout = null;
  let cartOperationInProgress = false;
  let operationQueue = [];
  
  /**
   * Queue system to prevent race conditions between cart operations
   * 
   * This ensures that cart operations (add, remove, quantity change) 
   * are processed one at a time in the order they were initiated.
   * This prevents issues where:
   * - Removing an item while quantity is updating
   * - Multiple quantity changes happening simultaneously
   * - Operations conflicting with each other causing cart corruption
   * 
   * Each operation is queued and waits for the previous one to complete
   * before executing. Visual feedback (opacity + pointer-events) prevents
   * users from clicking multiple times during updates.
   */
  async function queueCartOperation(operation) {
    return new Promise((resolve, reject) => {
      operationQueue.push({ operation, resolve, reject });
      processQueue();
    });
  }
  
  async function processQueue() {
    if (cartOperationInProgress || operationQueue.length === 0) {
      return;
    }
    
    cartOperationInProgress = true;
    
    // Add visual indicator that cart is being updated
    const cartDrawer = document.querySelector('.scd__content');
    if (cartDrawer) {
      cartDrawer.style.pointerEvents = 'none';
      cartDrawer.style.opacity = '0.7';
    }
    
    const { operation, resolve, reject } = operationQueue.shift();
    
    try {
      const result = await operation();
      resolve(result);
    } catch (error) {
      reject(error);
    } finally {
      cartOperationInProgress = false;
      
      // Remove visual indicator
      if (cartDrawer) {
        cartDrawer.style.pointerEvents = '';
        cartDrawer.style.opacity = '';
      }
      
      // Process next operation in queue
      if (operationQueue.length > 0) {
        setTimeout(processQueue, 50);
      }
    }
  }
  
  // Wrap customDiscountLogic to prevent errors with missing elements
  window.safeSetInnerHTML = function(selector, content) {
    const element = document.querySelector(selector);
    if (element && content !== undefined) {
      element.innerHTML = content;
      return true;
    }
    return false;
  };
  
  // Prevent app.js from handling cart button clicks (we'll handle them)
  document.addEventListener('click', function(event) {
    if (event.target.matches('.scd-item__btn')) {
      // Mark this event as handled by our script
      event.cartHandledByFix = true;
      // Try to stop propagation in capture phase
      event.stopPropagation();
    }
  }, true); // Use capture phase to run before app.js

  function updateAllCartCounts(count) {
    // Force immediate update of all cart count elements
    requestAnimationFrame(() => {
      const cartCounts = document.querySelectorAll('.sf-cart-count');
      cartCounts.forEach(counter => {
        counter.textContent = count;
        counter.setAttribute('data-count', count);
        // Force visibility of font-medium class
        if (counter.classList.contains('font-medium')) {
          counter.style.fontWeight = '500';
        }
      });
      
      const cartCountHeader = document.querySelector('.scd__header h3');
      if (cartCountHeader) {
        cartCountHeader.textContent = `MY CART (${count})`;
      }
      
      const otherCartCounts = document.querySelectorAll('.cart-count, .cart-counter, [data-cart-count]');
      otherCartCounts.forEach(counter => {
        if (counter.tagName === 'INPUT') {
          counter.value = count;
        } else {
          counter.textContent = count;
        }
        counter.setAttribute('data-count', count);
      });
    });
    
    const emptyMessage = document.querySelector('.scd-empty-msg');
    const itemsContainer = document.querySelector('.scd__items');
    const cartDrawerFooter = document.querySelector('.scd__footer');
    
    if (count === 0) {
      if (emptyMessage) {
        emptyMessage.style.display = 'block';
        emptyMessage.classList.remove('hidden');
      }
      if (itemsContainer) {
        itemsContainer.style.display = 'none';
        itemsContainer.classList.add('hidden');
      }
      if (cartDrawerFooter) {
        cartDrawerFooter.classList.add('hidden');
        cartDrawerFooter.style.display = 'none';
      }
    } else {
      if (emptyMessage) {
        emptyMessage.style.display = 'none';
        emptyMessage.classList.add('hidden');
      }
      if (itemsContainer) {
        itemsContainer.style.display = 'block';
        itemsContainer.classList.remove('hidden');
      }
      if (cartDrawerFooter) {
        cartDrawerFooter.classList.remove('hidden');
        cartDrawerFooter.style.display = 'block';
      }
    }
  }

  function openCartDrawer() {
    const cartDrawer = document.querySelector('.scd__wrapper');
    const cartDrawerContent = document.querySelector('.scd__content');
    
    if (cartDrawer && cartDrawerContent) {
      if (window.innerWidth < 770) {
        document.documentElement.classList.add('prevent-scroll');
        cartDrawer.classList.add('mobile');
      }
      
      cartDrawer.classList.remove('hidden');
      
      requestAnimationFrame(() => {
        cartDrawer.style.setProperty('--tw-bg-opacity', '0.5');
        cartDrawerContent.classList.remove('translate-x-full');
      });
    }
  }

  function closeCartDrawer() {
    const cartDrawer = document.querySelector('.scd__wrapper');
    const cartDrawerContent = document.querySelector('.scd__content');
    
    if (cartDrawer && cartDrawerContent) {
      cartDrawer.style.setProperty('--tw-bg-opacity', '0');
      cartDrawerContent.classList.add('translate-x-full');
      
      setTimeout(() => {
        cartDrawer.classList.add('hidden');
        cartDrawer.classList.remove('mobile');
        document.documentElement.classList.remove('prevent-scroll');
      }, 300);
    }
  }

  async function fetchCartSection() {
    try {
      const response = await fetch('/?section_id=cart-drawer');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.text();
    } catch (error) {
      console.error('Error fetching cart section:', error);
      return null;
    }
  }

  const debouncedUpdateCartDrawer = debounce(async () => {
    if (isUpdating) {
      updateQueued = true;
      return;
    }

    isUpdating = true;
    
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      updateAllCartCounts(cart.item_count);

      const cartHTML = await fetchCartSection();
      if (cartHTML) {
        const tempContainer = document.createElement('div');
        tempContainer.innerHTML = cartHTML;
        
        const cartDrawer = document.querySelector('.scd__wrapper');
        if (!cartDrawer) {
          isUpdating = false;
          return;
        }

        const newHeader = tempContainer.querySelector('.scd__header');
        const currentHeader = document.querySelector('.scd__header');
        if (newHeader && currentHeader) {
          currentHeader.innerHTML = newHeader.innerHTML;
        }
        
        const newBody = tempContainer.querySelector('.scd__body');
        const currentBody = document.querySelector('.scd__body');
        if (newBody && currentBody) {
          currentBody.innerHTML = newBody.innerHTML;
        }

        const newSummary = tempContainer.querySelector('.scd__summary');
        const currentSummary = document.querySelector('.scd__summary');
        if (newSummary && currentSummary) {
          currentSummary.innerHTML = newSummary.innerHTML;
        }

        const newFooter = tempContainer.querySelector('.scd__footer');
        const currentFooter = document.querySelector('.scd__footer');
        if (newFooter && currentFooter) {
          currentFooter.innerHTML = newFooter.innerHTML;
          
          if (cart.item_count > 0) {
            currentFooter.classList.remove('hidden');
            currentFooter.style.display = 'block';
          } else {
            currentFooter.classList.add('hidden');
            currentFooter.style.display = 'none';
          }
        }

        const checkoutButton = currentFooter?.querySelector('[name="checkout"]');
        const subtotal = currentFooter?.querySelector('.scd__summary-subtotal');
        
        if (cart.item_count > 0) {
          if (checkoutButton) {
            checkoutButton.style.display = 'block';
            checkoutButton.classList.remove('hidden');
          }
          if (subtotal) {
            subtotal.style.display = 'block';
            subtotal.classList.remove('hidden');
          }
        }
        
        // Trigger custom discount logic reinit after DOM update
        setTimeout(() => {
          if (typeof window.initCustomDiscountLogic === 'function') {
            window.initCustomDiscountLogic();
          }
        }, 100);
      }
    } catch (error) {
      console.error('Error updating cart drawer:', error);
    } finally {
      isUpdating = false;
      if (updateQueued) {
        updateQueued = false;
        setTimeout(debouncedUpdateCartDrawer, 300);
      }
    }
  }, 100);

  async function addToCart(variantId, quantity = 1) {
    return queueCartOperation(async () => {
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            items: [{
              id: variantId,
              quantity: quantity
            }]
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const item = await response.json();
        
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        
        // Update counts immediately
        updateAllCartCounts(cart.item_count);

        // Refresh the cart drawer immediately (no debounce) to show the new item
        const cartHTML = await fetchCartSection();
        if (cartHTML) {
          const tempContainer = document.createElement('div');
          tempContainer.innerHTML = cartHTML;
          
          const cartDrawer = document.querySelector('.scd__wrapper');
          if (cartDrawer) {
            const newHeader = tempContainer.querySelector('.scd__header');
            const currentHeader = document.querySelector('.scd__header');
            if (newHeader && currentHeader) {
              currentHeader.innerHTML = newHeader.innerHTML;
            }
            
            const newBody = tempContainer.querySelector('.scd__body');
            const currentBody = document.querySelector('.scd__body');
            if (newBody && currentBody) {
              currentBody.innerHTML = newBody.innerHTML;
            }

            const newSummary = tempContainer.querySelector('.scd__summary');
            const currentSummary = document.querySelector('.scd__summary');
            if (newSummary && currentSummary) {
              currentSummary.innerHTML = newSummary.innerHTML;
            }

            const newFooter = tempContainer.querySelector('.scd__footer');
            const currentFooter = document.querySelector('.scd__footer');
            if (newFooter && currentFooter) {
              currentFooter.innerHTML = newFooter.innerHTML;
              
              if (cart.item_count > 0) {
                currentFooter.classList.remove('hidden');
                currentFooter.style.display = 'block';
              }
            }
            
            // Trigger custom discount logic reinit after DOM update
            setTimeout(() => {
              if (typeof window.initCustomDiscountLogic === 'function') {
                window.initCustomDiscountLogic();
              }
            }, 100);
          }
        }

        // Emit events
        if (typeof MinimogEvents !== 'undefined') {
          MinimogEvents.emit('ON_ITEM_ADDED', item);
          MinimogEvents.emit('ON_CART_UPDATE', cart);
        }

        openCartDrawer();

        return item;
      } catch (error) {
        console.error('Error adding to cart:', error);
        return null;
      }
    });
  }

  async function removeItem(itemKey, itemElement) {
    // Immediately hide the item for instant feedback
    if (itemElement) {
      itemElement.style.opacity = '0.5';
      itemElement.style.pointerEvents = 'none';
      itemElement.style.transition = 'opacity 0.2s ease';
    }
    
    return queueCartOperation(async () => {
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: itemKey,
            quantity: 0
          })
        });

        if (!response.ok) {
          // Revert visual feedback on error
          if (itemElement) {
            itemElement.style.opacity = '1';
            itemElement.style.pointerEvents = '';
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const cart = await response.json();
        
        // Update counts immediately
        updateAllCartCounts(cart.item_count);

        // Fully refresh the cart drawer immediately (no debounce for removals)
        await fetchCartSection().then(async (cartHTML) => {
          if (cartHTML) {
            const tempContainer = document.createElement('div');
            tempContainer.innerHTML = cartHTML;
            
            const cartDrawer = document.querySelector('.scd__wrapper');
            if (!cartDrawer) return;

            const newHeader = tempContainer.querySelector('.scd__header');
            const currentHeader = document.querySelector('.scd__header');
            if (newHeader && currentHeader) {
              currentHeader.innerHTML = newHeader.innerHTML;
            }
            
            const newBody = tempContainer.querySelector('.scd__body');
            const currentBody = document.querySelector('.scd__body');
            if (newBody && currentBody) {
              currentBody.innerHTML = newBody.innerHTML;
            }

            const newSummary = tempContainer.querySelector('.scd__summary');
            const currentSummary = document.querySelector('.scd__summary');
            if (newSummary && currentSummary) {
              currentSummary.innerHTML = newSummary.innerHTML;
            }

            const newFooter = tempContainer.querySelector('.scd__footer');
            const currentFooter = document.querySelector('.scd__footer');
            if (newFooter && currentFooter) {
              currentFooter.innerHTML = newFooter.innerHTML;
              
              if (cart.item_count > 0) {
                currentFooter.classList.remove('hidden');
                currentFooter.style.display = 'block';
              } else {
                currentFooter.classList.add('hidden');
                currentFooter.style.display = 'none';
              }
            }

            // Trigger custom discount logic reinit after DOM update
            setTimeout(() => {
              if (typeof window.initCustomDiscountLogic === 'function') {
                window.initCustomDiscountLogic();
              }
            }, 100);
          }
        });

        // Emit event
        if (typeof MinimogEvents !== 'undefined') {
          MinimogEvents.emit('ON_CART_UPDATE', cart);
        }

        return cart;
      } catch (error) {
        console.error('Error removing item:', error);
        // Revert visual feedback on error
        if (itemElement) {
          itemElement.style.opacity = '1';
          itemElement.style.pointerEvents = '';
        }
        return null;
      }
    });
  }

  // Quantity button handler - with prevention of double-firing (bubble phase)
  document.addEventListener('click', async function(event) {
    // Handle quantity buttons
    if (event.target.matches('.scd-item__btn') && !event.target.hasAttribute('data-processing')) {
      // Prevent clicks if operation already in progress
      if (cartOperationInProgress) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation(); // Stop other listeners on same element
      
      const button = event.target;
      const item = button.closest('.scd-item');
      const input = item?.querySelector('.scd-item__qty_input');
      
      if (item && input) {
        // Mark button as processing to prevent double-clicks
        button.setAttribute('data-processing', 'true');
        button.disabled = true;
        
        const currentQty = parseInt(input.value) || 0;
        const isIncrement = button.dataset.qtyChange === 'inc' || button.textContent.includes('+');
        const newQty = isIncrement ? currentQty + 1 : Math.max(1, currentQty - 1);
        
        if (newQty !== currentQty) {
          const itemKey = item.dataset.id;
          
          // Update input immediately for instant feedback
          input.value = newQty;
          
          // Queue this operation to prevent race conditions
          queueCartOperation(async () => {
            try {
              const response = await fetch('/cart/change.js', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  id: itemKey,
                  quantity: newQty
                })
              });
              
              if (response.ok) {
                const cart = await response.json();
                
                // Update counts immediately
                updateAllCartCounts(cart.item_count);
                
                // Update the actual cart item in the DOM immediately
                const cartItem = cart.items.find(i => i.key === itemKey);
                if (cartItem) {
                  input.value = cartItem.quantity;
                }
                
                // Update drawer content fully (prices, subtotals, discount progress, etc)
                const cartHTML = await fetchCartSection();
                if (cartHTML) {
                  const tempContainer = document.createElement('div');
                  tempContainer.innerHTML = cartHTML;
                  
                  // Update header with cart count
                  const newHeader = tempContainer.querySelector('.scd__header');
                  const currentHeader = document.querySelector('.scd__header');
                  if (newHeader && currentHeader) {
                    currentHeader.innerHTML = newHeader.innerHTML;
                  }
                  
                  // Update body (includes items and shipping progress)
                  const newBody = tempContainer.querySelector('.scd__body');
                  const currentBody = document.querySelector('.scd__body');
                  if (newBody && currentBody) {
                    currentBody.innerHTML = newBody.innerHTML;
                  }
                  
                  // Update summary/footer
                  const newSummary = tempContainer.querySelector('.scd__summary');
                  const currentSummary = document.querySelector('.scd__summary');
                  if (newSummary && currentSummary) {
                    currentSummary.innerHTML = newSummary.innerHTML;
                  }
                  
                  const newFooter = tempContainer.querySelector('.scd__footer');
                  const currentFooter = document.querySelector('.scd__footer');
                  if (newFooter && currentFooter) {
                    currentFooter.innerHTML = newFooter.innerHTML;
                    if (cart.item_count > 0) {
                      currentFooter.classList.remove('hidden');
                      currentFooter.style.display = 'block';
                    }
                  }
                }
                
                // Emit events
                if (typeof MinimogEvents !== 'undefined') {
                  MinimogEvents.emit('ON_CART_UPDATE', cart);
                }
                
                return cart;
              } else {
                // Revert on error
                input.value = currentQty;
                console.error('Failed to update cart quantity');
                return null;
              }
            } catch (error) {
              // Revert on error
              input.value = currentQty;
              console.error('Error updating cart:', error);
              return null;
            }
          }).finally(() => {
            // Re-enable button after operation completes
            button.removeAttribute('data-processing');
            button.disabled = false;
          });
        } else {
          button.removeAttribute('data-processing');
          button.disabled = false;
        }
      }
      return;
    }
  }, true); // Use capture phase to intercept before other handlers
  
  // Capture-phase handler for "You May Also Like" add-to-cart buttons
  // This runs BEFORE cart-recommendations.js handler to prevent duplicates
  document.addEventListener('click', async function(event) {
    if (event.target.matches('.pf-nst011-add-to-cart') || event.target.closest('.pf-nst011-add-to-cart')) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation(); // Prevent cart-recommendations.js handler from firing
      
      // Mark event as handled to prevent cart-recommendations.js
      event._handledByCartFix = true;
      
      // Prevent if operation already in progress
      if (cartOperationInProgress) {
        return;
      }
      
      const button = event.target.closest('.pf-nst011-add-to-cart');
      const variantId = button.dataset.variantId;
      
      if (variantId) {
        try {
          button.classList.add('loading');
          button.disabled = true;
          
          const item = await addToCart(variantId);
          
          if (item) {
            button.classList.add('success');
            setTimeout(() => {
              button.classList.remove('success');
            }, 2000);
          }
        } catch (error) {
          console.error('Error adding item to cart:', error);
          button.classList.add('error');
          setTimeout(() => {
            button.classList.remove('error');
          }, 2000);
        } finally {
          button.classList.remove('loading');
          button.disabled = false;
        }
      }
    }
  }, true); // Use capture phase to run BEFORE cart-recommendations.js
  
  // Separate async event listener for remove button and other actions
  document.addEventListener('click', async function(event) {
    // Remove button handler
    if (event.target.matches('.scd-item__remove') || event.target.closest('.scd-item__remove')) {
      event.preventDefault();
      
      // Prevent multiple operations at once
      if (cartOperationInProgress) {
        return;
      }
      
      const removeButton = event.target.closest('.scd-item__remove');
      const item = removeButton.closest('.scd-item');
      
      if (item) {
        const itemKey = item.dataset.id;
        
        if (itemKey) {
          try {
            removeButton.classList.add('loading');
            removeButton.disabled = true;
            
            // Pass the item element for immediate visual feedback
            const cart = await removeItem(itemKey, item);
            
            // Item will be removed from DOM by drawer refresh in removeItem()
          } catch (error) {
            console.error('Error removing item:', error);
          } finally {
            // Button will be removed by drawer refresh, no need to re-enable
          }
        }
      }
    }
    
    if (event.target.matches('.sf-cart-icon') || event.target.closest('.sf-cart-icon')) {
      event.preventDefault();
      openCartDrawer();
    }
  });

  document.addEventListener('click', function(event) {
    if (event.target.matches('.scd__close') || event.target.closest('.scd__close')) {
      event.preventDefault();
      closeCartDrawer();
    }

    if (event.target.matches('.scd__wrapper')) {
      closeCartDrawer();
    }
  });

  if (typeof MinimogEvents !== 'undefined') {
    MinimogEvents.subscribe('ON_CART_UPDATE', (cart) => {
      if (cart) {
        // Update counts immediately for instant feedback
        updateAllCartCounts(cart.item_count);
        
        // Update the MY CART header immediately without full drawer refresh
        const cartCountHeader = document.querySelector('.scd__header h3');
        if (cartCountHeader) {
          cartCountHeader.textContent = `MY CART (${cart.item_count})`;
        }
        
        // Don't trigger full drawer refresh here - we handle that in our button click
      }
    });
  }

  if (typeof Shopify !== 'undefined') {
    const originalOnItemAdded = Shopify.onItemAdded;
    Shopify.onItemAdded = async function(item) {
      try {
        // Fetch updated cart for count
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        
        // Update counts immediately
        updateAllCartCounts(cart.item_count);
        
        // Refresh drawer to show new item
        await debouncedUpdateCartDrawer();
        
        // Call original handler if exists
        if (originalOnItemAdded) {
          originalOnItemAdded(item, false);
        }
        
        // Emit event for other listeners
        if (typeof MinimogEvents !== 'undefined') {
          MinimogEvents.emit('ON_CART_UPDATE', cart);
        }
        
        openCartDrawer();
      } catch (error) {
        console.error('Error in onItemAdded:', error);
      }
    };
  }

  document.addEventListener('cart:update', (event) => {
    if (event.detail && event.detail.cart) {
      updateAllCartCounts(event.detail.cart.item_count);
    } else {
      // Fetch cart if not provided
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => updateAllCartCounts(cart.item_count))
        .catch(console.error);
    }
  });
  
  if (window.location.pathname.includes('/cart')) {
    
    const cartPageQtyInputs = document.querySelectorAll('input[name="updates[]"]');
    
    const updateCartCountFromServer = async () => {
      try {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        updateAllCartCounts(cart.item_count);
      } catch (error) {
        console.error('Error updating cart count on cart page:', error);
      }
    };
    
    const cartIncButtons = document.querySelectorAll('.quantity-increment, .js-qty__adjust--plus, [data-action="increase-quantity"]');
    const cartDecButtons = document.querySelectorAll('.quantity-decrement, .js-qty__adjust--minus, [data-action="decrease-quantity"]');
    
    
    const handleQuantityButtonClick = () => {
      setTimeout(updateCartCountFromServer, 500);
    };
    
    cartIncButtons.forEach(btn => {
      btn.addEventListener('click', handleQuantityButtonClick);
    });
    
    cartDecButtons.forEach(btn => {
      btn.addEventListener('click', handleQuantityButtonClick);
    });
    
    cartPageQtyInputs.forEach(input => {
      const originalChangeHandler = input.onchange;
      
      input.onchange = async function(e) {
        if (originalChangeHandler) {
          originalChangeHandler.call(this, e);
        }
        
        setTimeout(updateCartCountFromServer, 500);
      };
    });
    
    const cartForm = document.querySelector('form[action="/cart"]');
    if (cartForm) {
      cartForm.addEventListener('submit', function(e) {
        setTimeout(updateCartCountFromServer, 500);
      });
    }
    
    updateCartCountFromServer();
  }
  
  // Initial cart count sync
  fetch('/cart.js')
    .then(response => response.json())
    .then(cart => {
      if (cart) {
        updateAllCartCounts(cart.item_count);
      }
    })
    .catch(console.error);
    
}); 